import { useEffect, useState } from "react";
import api, { formatDate, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Mail, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function MensajesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/messages");
      setItems(data);
      setError(null);
    } catch (e) {
      setError(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const markRead = async (id) => {
    try {
      await api.post(`/admin/messages/${id}/read`);
      setItems((prev) => prev.map((m) => (m.id === id ? { ...m, read: true } : m)));
      toast.success("Marcado como leído");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  if (error) {
    return (
      <div className="max-w-xl space-y-4" data-testid="mensajes-page">
        <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">
          Mensajes
        </h1>
        <Card className="p-6 bg-amber-50 border-amber-200">
          <p className="text-sm text-slate-700">
            <b>Acceso restringido.</b> Solo la cuenta administradora puede ver
            los mensajes de contacto recibidos desde la landing.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mensajes-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">
          Mensajes recibidos
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Consultas enviadas desde el formulario de contacto de la landing.
        </p>
      </div>

      <Card className="p-5">
        {loading ? (
          <div className="py-10 text-center text-slate-500">Cargando...</div>
        ) : items.length === 0 ? (
          <div className="text-center py-14">
            <MessageSquare className="w-10 h-10 text-slate-300 mx-auto" />
            <p className="mt-3 text-sm text-slate-500">
              Aún no has recibido mensajes de contacto.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((m) => (
              <div
                key={m.id}
                className={`py-4 flex items-start gap-4 ${m.read ? "opacity-70" : ""}`}
                data-testid={`msg-${m.id}`}
              >
                <div
                  className={`w-10 h-10 rounded-full grid place-items-center shrink-0 ${
                    m.read ? "bg-slate-100 text-slate-400" : "bg-blue-100 text-blue-600"
                  }`}
                >
                  <Mail className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-medium text-slate-900">{m.name}</div>
                    <a
                      href={`mailto:${m.email}`}
                      className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                    >
                      {m.email}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                    {!m.read && (
                      <Badge className="bg-blue-600 text-white text-[10px]">Nuevo</Badge>
                    )}
                    <div className="ml-auto text-xs text-slate-500">
                      {formatDate(m.created_at)}
                    </div>
                  </div>
                  <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap leading-relaxed">
                    {m.message}
                  </p>
                  <div className="flex gap-2 mt-3">
                    <a href={`mailto:${m.email}`}>
                      <Button size="sm" variant="outline">
                        <Mail className="w-3 h-3 mr-1" /> Responder
                      </Button>
                    </a>
                    {!m.read && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => markRead(m.id)}
                        data-testid={`mark-read-${m.id}`}
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Marcar leído
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
